import "./utils/styles.css";
import 'ckeditor5/ckeditor5.css';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import { useEffect, useState } from "react";
import changeCase from "case";
import {
    ClassicEditor,
    AccessibilityHelp,
    Alignment,
    AutoImage,
    Autosave,
    BlockQuote,
    Bold,
    Code,
    CodeBlock,
    GeneralHtmlSupport,
    Essentials,
    FontBackgroundColor,
    FontColor,
    Heading,
    HorizontalLine,
    ImageBlock,
    ImageCaption,
    ImageInline,
    ImageInsert,
    ImageInsertViaUrl,
    ImageResize,
    ImageStyle,
    ImageTextAlternative,
    ImageToolbar,
    ImageUpload,
    Italic,
    Link,
    LinkImage,
    List,
    MediaEmbed,
    Paragraph,
    RemoveFormat,
    SimpleUploadAdapter,
    SourceEditing,
    Strikethrough,
    Underline,
    Undo,
} from 'ckeditor5';
import UploadAdapter from "./utils/upload-adapter";
import { Typography } from "@mui/material";
import { paste } from "@testing-library/user-event/dist/paste";

export function CKRichTextEditor({ formik, name, required = false, showName = true, onBlur, onChange, value, isError, error }) {
    
    const [isLayoutReady, setIsLayoutReady] = useState(false);
    const rteError = formik ? (formik.touched[name] && formik.errors[name]) : isError;

    const customAdapterPlugin = function (editor) {
        editor.plugins.get('FileRepository').createUploadAdapter = function (loader) {
            return new UploadAdapter(loader);
        };
    }

    const editorConfig = {
        extraPlugins: [customAdapterPlugin],
        toolbar: {
            items: [
                'undo',
                'redo',
                '|',
                'sourceEditing',
                '|',
                'Heading',
                'fontColor',
                'fontBackgroundColor',
                '|',
                'bold',
                'italic',
                'underline',
                'strikethrough',
                'removeFormat',
                '|',
                'link',
                'insertImage',
                'MediaEmbed',
                'blockQuote',
                '|',
                'alignment',
                '|',
                'bulletedList',
                'numberedList',
            ],
            shouldNotGroupWhenFull: false
        },
        plugins: [
            AccessibilityHelp,
            Alignment,
            AutoImage,
            Autosave,
            BlockQuote,
            Bold,
            Code,
            CodeBlock,
            Essentials,
            FontBackgroundColor,
            FontColor,
            GeneralHtmlSupport,
            Heading,
            HorizontalLine,
            ImageBlock,
            ImageCaption,
            ImageInline,
            ImageInsert,
            ImageInsertViaUrl,
            ImageResize,
            ImageStyle,
            ImageTextAlternative,
            ImageToolbar,
            ImageUpload,
            Italic,
            Link,
            LinkImage,
            List,
            MediaEmbed,
            Paragraph,
            RemoveFormat,
            SimpleUploadAdapter,
            SourceEditing,
            Strikethrough,
            Underline,
            Undo
        ],
        htmlSupport: {
            allow: [
                {
                    name: /^.*$/,
                    styles: true,
                    attributes: true,
                    classes: true
                }
            ]
        },
        fontSize: {
            options: [10, 12, 14, 'default', 18, 20, 22],
            supportAllValues: true
        },
        heading: {
            options: [
                { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
                { model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
                { model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
                { model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' },
                { model: 'heading4', view: 'h4', title: 'Heading 4', class: 'ck-heading_heading4' },
                { model: 'heading5', view: 'h5', title: 'Heading 5', class: 'ck-heading_heading5' },
                { model: 'heading6', view: 'h6', title: 'Heading 6', class: 'ck-heading_heading6' }
            ]
        },
        image: {
            toolbar: [
                'toggleImageCaption',
                'imageTextAlternative',
                '|',
                'imageStyle:inline',
                'imageStyle:wrapText',
                'imageStyle:breakText',
                '|',
                'resizeImage'
            ]
        },
        initialData: "",
        link: {
            addTargetToExternalLinks: true,
            defaultProtocol: 'https://',
            decorators: {
                toggleDownloadable: {
                    mode: 'manual',
                    label: 'Downloadable',
                    attributes: {
                        download: 'file'
                    }
                }
            }
        },
        placeholder: 'Type or paste your content here!',
        mediaEmbed: { previewsInData: true },
        allowedContent: true,
        disallowedContent: 'img{width,height}',
        removePlugins: ['pastefromword'],  // Ensure this is removed
        clipboard: {
            removeFormatAttributes: true,  // Ensure formatting attributes are removed
            removeFormatOnPaste: true  // Remove formatting on paste
        },
        paste: {
            remove: [
                'style',
                'class',
                'id' // Remove if necessary
            ]
        },
        dataProcessor: {
            toDataFormat: function (data) {
                // Customize how the data is processed if necessary
                return data;
            },
            fromDataFormat: function (data) {
                // Customize how the data is processed if necessary
                return data;
            },
            
        },
        forcePasteAsPlainText:true,
        on: {
            ready: (editor) => {
                const viewDocument = editor.editing.view.document;

                // Track the paste event
                viewDocument.on('paste', (event, data) => {
                    console.log('Content pasted');
                    //setIsPasting(true); // Set flag to true when a paste happens
                });
            }
        }
    };

    useEffect(() => {
        setIsLayoutReady(true);
        
        return () => setIsLayoutReady(false);
    }, []);


    return (
        <>
            {showName && (
                <Typography variant="body2" sx={{ fontSize: "1rem", marginBottom: "1px" }}>
                    {changeCase.title(name)}
                    {required ? " *" : " "}
                </Typography>
            )}
            <div style={{
                border: `1px solid ${rteError ? 'rgb(255, 86, 48)' : 'rgba(23, 23, 23, 0.256)'}`,
                borderRadius: "6px",
            }}>
                {isLayoutReady && (
                    <CKEditor
                        editor={ClassicEditor}
                        config={editorConfig}
                        onChange={(event, editor) => {
                            console.log("Something changed!", editor);
                            
                            if (onChange) {
                                onChange.change(editor.getData())
                            } else {
                                const editorString = editor.getData().replace(/style="aspect-ratio:\d+\/\d+;"/, "") //remove aspect ratio
                                                           .replace(/<img([^>]*)\s+width="\d+"\s+height="\d+"([^>]*)>/, '<img$1$2>');//remove width and height
                                formik.setFieldValue(name,editorString)
                            }
                        }}
                        data={formik?.values[name] || ""}
                        onBlur={() => {
                            if (onBlur) onBlur();
                            else {
                                formik.setFieldTouched(name, true);
                            }
                        }}
                        onInit={(editor) => {
                            editor.model.document.on("change:data", () => {
                                const data = editor.getData().replace(/style="aspect-ratio:\d+\/\d+;"/, "") // Remove aspect ratio
                                                           .replace(/<img([^>]*)\s+width="\d+"\s+height="\d+"([^>]*)>/, '<img$1$2>'); // Remove width and height
                                formik.setFieldValue(name, data);
                            });

                            editor.model.document.on("change", () => {
                                console.log("Document has changed");
                            });

                            editor.editing.view.document.on("clipboardInput", (evt) => {
                                evt.preventDefault();
                                console.log("Clip board event");
                                
                                const content = evt.dataTransfer.getData("text/plain");
                                editor.model.change((writer) => {
                                    console.log("content", content);
                                    
                                    const insertPosition = editor.model.document.selection.getFirstPosition();
                                    writer.insertText(content, insertPosition);
                                });
                            });
                        }}
                    />
                )}
            </div>
            {rteError ? (
                <Typography variant="caption" color={"error"} sx={{ my: 1 }}>
                    {formik ? formik.errors[name] : error}
                </Typography>
            ):<Typography variant="caption" 
                          sx={{ my: 1 }}>Use <b>(ctr / cmd)+shit+v</b> to remove styles on pasting</Typography>}
        </>
    );
}
